import styled, { useTheme } from 'styled-components';
import { Box } from 'reflexbox';
import { Bar } from 'react-chartjs-2';

const Container = styled(Box)<{ height: number }>`
	position: relative;
	height: ${(props) => props.height}px;
`;

const Graph = styled(Box)`
	position: absolute;
	width: 100%;
	height: 100%;
`;

export interface BarGraphDataset {
	label: string;
	color: string;
	data: number[];
}

export default function BarMetric({
	labels,
	values,
	tooltip,
	height,
	padding,
	yMin,
	yMax,
	yStepSize,
	yFormat,
}: {
	labels: string[];
	values: BarGraphDataset[];
	tooltip?: boolean;
	height?: number;
	padding?: number;
	yMin?: number;
	yMax?: number;
	yStepSize?: number;
	yFormat?: (x: number) => string;
}) {
	const theme = useTheme() as any;

	const graphData = {
		labels,
		datasets: values.map(({ label, color, data }) => ({
			label,
			stack: 'Stack 0',
			data,
			fill: false,
			backgroundColor: color,
			borderColor: color,
		})),
	};

	const options = {
		responsive: true,
		layout: {
			padding: padding ?? 10,
		},
		scales: {
			y: {
				beginAtZero: true,
				suggestedMin: yMin,
				suggestedMax: yMax,
				grid: {
					color: theme.colors.whiteBlue,
					drawBorder: false,
				},
				ticks: {
					indexLabel: '#percent%',
					precision: 0,
					color: theme.colors.slateBlue,
					stepSize: yStepSize,
					autoSkip: typeof yStepSize !== 'number',
					callback: yFormat,
					font: {
						weight: 'bold',
						size: 12,
						family: 'Source Sans Pro',
					},
				},
			},
			x: {
				grid: {
					display: false,
					drawBorder: false,
				},
				ticks: {
					color: theme.colors.slateBlue,
					font: {
						weight: 'bold',
						size: 12,
						family: 'Source Sans Pro',
					},
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: tooltip ?? true,
				callbacks: {
					label: function (context: any) {
						return yFormat?.(context.parsed.y) ?? context.parsed.y;
					},
				},
			},
		},
		maintainAspectRatio: false,
	};

	return (
		<Box pt={typeof padding === 'number' ? 0 : 3}>
			<Container height={height ?? 142}>
				<Graph>
					<Bar data={graphData} options={options} height={height ?? 142} />
				</Graph>
			</Container>
		</Box>
	);
}
